import type { ComponentProps, FC } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import type { OutlinedInputProps, SxProps, Theme } from '@mui/material';
import { TextField as MuiTextField, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import type { OverridableStringUnion } from '@mui/types';
import type { TextFieldPropsSizeOverrides } from '@mui/material/TextField/TextField';
import type { InputBaseProps } from '@mui/material/InputBase';
import { get as getByPath } from 'lodash-es';

interface TextFieldProps {
  name: string;
  label?: string;
  inputLabel?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  handleChange?: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  restProps?: ComponentProps<typeof MuiTextField>;
  maxLength?: number;
  type?: string;
  InputProps?: Partial<OutlinedInputProps>;
  inputProps?: InputBaseProps['inputProps'];
  sx?: SxProps<Theme>;
  size?: OverridableStringUnion<
    'small' | 'medium',
    TextFieldPropsSizeOverrides
  >;
  multiline?: boolean;
  errorAsTooltip?: boolean;
  dataTestId?: string;
}

const TextField: FC<TextFieldProps> =
  ({
     name,
     label,
     inputLabel,
     placeholder,
     helperText,
     required,
     disabled,
     handleChange,
     maxLength,
     sx,
     inputProps,
     InputProps,
     errorAsTooltip,
     dataTestId,
     ...restProps
   }) => {
    const { control, formState, trigger } = useFormContext<FieldValues>();
    const error = getByPath(formState.errors, name);

    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const { value, onChange } = field;
          return (
            <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
              {label && !inputLabel && (
                <Typography variant="subtitle2" mb={1}>
                  {label}
                  {required && '*'}
                </Typography>
              )}
              <Tooltip title={errorAsTooltip ? (error?.message as string) : ''} leaveDelay={100}>
                <MuiTextField
                  {...field}
                  value={value ?? ''}
                  fullWidth
                  label={inputLabel ? `${inputLabel}${required ? '*' : ''}` : null}
                  error={!!error}
                  disabled={disabled}
                  placeholder={placeholder}
                  helperText={helperText}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    ...sx,
                  }}
                  {...restProps}
                  variant="outlined"
                  onChange={async (event) => {
                    onChange(event);
                    await trigger(name);
                    handleChange && handleChange(event);
                  }}
                  inputProps={{
                    maxLength: maxLength, ...inputProps,
                    'data-testid': dataTestId,
                    sx: { borderRadius: 0, ...inputProps?.sx },
                  }}
                  InputProps={InputProps}
                />
              </Tooltip>
              {!errorAsTooltip && error?.message && (
                <Typography
                  variant="subtitle1"
                  fontSize={12}
                  sx={{ mt: 0.25 }}
                  color="error"
                >
                  {error.message as string}
                </Typography>
              )}
            </Box>
          );
        }}
      />
    );
  };

export default TextField;
